import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authService } from '@/services/authService'
import { useAuthStore } from '@/stores/authStore'
import { getSafePostAuthReturnUrl, takePostAuthReturnUrl } from '@/lib/returnUrl'

interface SSOHandlerProps {
  children: React.ReactNode
}

export const SSOHandler = ({ children }: SSOHandlerProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('auth')
  // Only show checking state if there's actually a token in the URL
  const hasToken = new URLSearchParams(location.search).has('token')
  const [isCheckingSSO, setIsCheckingSSO] = useState(hasToken)
  const [ssoError, setSSoError] = useState<string | null>(null)
  const { setAuthenticated, setUser, setAccessToken, setRefreshToken } = useAuthStore()

  useEffect(() => {
    const checkForSSOToken = async () => {
      // Parse query parameters from current URL
      const params = new URLSearchParams(location.search)
      const token = params.get('token')
      const returnUrl = params.get('returnUrl')

      // If no token, not an SSO login - render normally
      if (!token) {
        setIsCheckingSSO(false)
        return
      }

      // Only process SSO tokens on specific routes (root, dashboard, or SSO callback)
      // Ignore tokens on other routes like /reset-password, /verify-email, etc.
      const ssoEnabledRoutes = ['/', '/login', '/login/callback', '/retail']
      if (!ssoEnabledRoutes.includes(location.pathname)) {
        setIsCheckingSSO(false)
        return
      }

      try {
        const safeReturnUrl = returnUrl ? getSafePostAuthReturnUrl(returnUrl) : undefined

        // Validate the SSO token with backend
        const response = await authService.validateSSOToken(token, safeReturnUrl)

        if (response.success && response.accessToken) {
          // Backend might return user as nested object OR at root level
          const userData = response.user || {
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            name: response.name,
          }
          
          // Construct name from available data
          let displayName = userData.name
          if (!displayName && userData.firstName && userData.lastName) {
            displayName = `${userData.firstName} ${userData.lastName}`.trim()
          } else if (!displayName && userData.firstName) {
            displayName = userData.firstName
          } else if (!displayName && userData.lastName) {
            displayName = userData.lastName
          } else if (!displayName) {
            // Extract name from email (e.g., "mark.eader@esc4.net" -> "Mark Eader")
            const emailName = userData.email?.split('@')[0] || ''
            const nameParts = emailName.split(/[._-]/).map(part => 
              part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            )
            displayName = nameParts.join(' ') || userData.email
          }
          
          const userForStore = {
            id: (userData as any).id ?? userData.email ?? '',
            email: userData.email ?? '',
            firstName: userData.firstName,
            lastName: userData.lastName,
            name: displayName,
            emailVerified: true,
            isActive: true,
          }
          
          // Update auth store
          setAuthenticated(true)
          setUser(userForStore)
          setAccessToken(response.accessToken)
          if (response.refreshToken) {
            setRefreshToken(response.refreshToken)
          }
          
          // Pre-fetch recommendations — display is gated by the LD enable-recommendations flag
          import('@/stores/recommendationsStore').then(({ useRecommendationsStore }) => {
            const email = response.user?.email
            if (email) {
              useRecommendationsStore.getState().fetchRecommendations(email, 10)
            }
          }).catch(() => {})

          // Clean up URL and honor only safe relative return targets.
          // Small delay to ensure localStorage and store are updated
          await new Promise(resolve => setTimeout(resolve, 100))
          
          // Verify everything is stored before navigating
          const storedAccessToken = localStorage.getItem('accessToken')
          const storedUser = localStorage.getItem('user')

          if (!storedAccessToken || !storedUser) {
            setSSoError(t('failedToStoreAuthTokens'))
            setIsCheckingSSO(false)
            return
          }
          
          const rememberedReturnUrl = takePostAuthReturnUrl()
          navigate(safeReturnUrl ?? rememberedReturnUrl ?? '/', { replace: true })
        } else {
          setSSoError(response.message || t('ssoAuthenticationFailed'))
          setIsCheckingSSO(false)
        }
      } catch (err: any) {
        setSSoError(err?.response?.data?.message || err?.message || t('ssoAuthenticationFailed'))
        setIsCheckingSSO(false)
      }
    }

    checkForSSOToken()
  }, [location.pathname, location.search, navigate, setAuthenticated, setUser, setAccessToken, setRefreshToken])

  // Show loading state while checking SSO
  if (isCheckingSSO && new URLSearchParams(location.search).get('token')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-transparent border-t-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('authenticatingSSO')}</p>
        </div>
      </div>
    )
  }

  // Show error state if SSO failed
  if (ssoError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12"
              style={{ color: '#7C2D37' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">{t('ssoAuthFailed')}</h2>
            <p className="mt-2 text-gray-600">{ssoError}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {t('returnToLogin')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // No SSO token or already processed - render children normally
  return <>{children}</>
}
